/**
 * The marks drawn on a report's picture of the screen (design study 05, 5A;
 * Jason, 2026-10-05: "reporting a bug or requesting a feature regardless should
 * be a nearly full screen popup so that you can circle/square the issue with
 * much more detail"): a circle, a box, an arrow, a pen line. Each is kept in
 * 0..1 of the picture, and drawn as the lines a hand would draw, the same on
 * the screen and burnt into the picture that is sent.
 */

export type MarkTool = 'circle' | 'box' | 'arrow' | 'pen';

export type Mark =
  | { kind: 'circle'; x: number; y: number; rx: number; ry: number }
  | { kind: 'box'; x: number; y: number; w: number; h: number }
  | { kind: 'arrow'; x1: number; y1: number; x2: number; y2: number }
  | { kind: 'pen'; points: [number, number][] };

export type Pt = { x: number; y: number };

/** The mark a drag from `a` to `b` makes with a tool; a pen line is grown point by point instead. */
export function markFrom(tool: Exclude<MarkTool, 'pen'>, a: Pt, b: Pt): Mark {
  if (tool === 'circle') return { kind: 'circle', x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, rx: Math.abs(b.x - a.x) / 2, ry: Math.abs(b.y - a.y) / 2 };
  if (tool === 'box') return { kind: 'box', x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) };
  return { kind: 'arrow', x1: a.x, y1: a.y, x2: b.x, y2: b.y };
}

/** Big enough to keep: a stray click draws nothing. */
export function worthKeeping(m: Mark): boolean {
  if (m.kind === 'circle') return m.rx > 0.01 && m.ry > 0.01;
  if (m.kind === 'box') return m.w > 0.01 && m.h > 0.01;
  if (m.kind === 'arrow') return Math.hypot(m.x2 - m.x1, m.y2 - m.y1) > 0.02;
  return m.points.length > 2;
}

/**
 * A circle drawn the way a hand draws it (style guide v2 §12: a practical,
 * irregular bengara ink stroke, not calligraphy): a little wobble, and the end
 * runs past the start.
 */
function inkLoop(m: Extract<Mark, { kind: 'circle' }>, sx: number, sy: number): [number, number][] {
  const points: [number, number][] = [];
  const phase = ((m.x * 7.3 + m.y * 13.1) % 1) * Math.PI * 2;
  const end = Math.PI * 2 + 0.5;
  for (let t = -0.2; t <= end; t += Math.PI / 40) {
    const wobble = 1 + 0.035 * Math.sin(3 * t + phase) + 0.02 * Math.cos(5 * t + phase);
    const drift = 1 + 0.07 * (t / end);
    points.push([(m.x + m.rx * wobble * drift * Math.cos(t)) * sx, (m.y + m.ry * wobble * drift * Math.sin(t) - m.ry * 0.05 * (t / end)) * sy]);
  }
  return points;
}

/** The lines a mark is drawn with, in a picture `sx` wide and `sy` tall. */
export function markLines(m: Mark, sx: number, sy: number): [number, number][][] {
  if (m.kind === 'circle') return [inkLoop(m, sx, sy)];
  if (m.kind === 'box') {
    const [x0, y0, x1, y1] = [m.x * sx, m.y * sy, (m.x + m.w) * sx, (m.y + m.h) * sy];
    return [[[x0, y0], [x1, y0], [x1, y1], [x0, y1], [x0, y0]]];
  }
  if (m.kind === 'arrow') {
    const [ax, ay, bx, by] = [m.x1 * sx, m.y1 * sy, m.x2 * sx, m.y2 * sy];
    const angle = Math.atan2(by - ay, bx - ax);
    const head = Math.min(Math.hypot(bx - ax, by - ay) * 0.35, Math.max(sx, sy) * 0.035);
    const wing = (side: 1 | -1): [number, number] => [bx - head * Math.cos(angle + side * 0.45), by - head * Math.sin(angle + side * 0.45)];
    return [[[ax, ay], [bx, by]], [wing(1), [bx, by], wing(-1)]];
  }
  return [m.points.map(([x, y]): [number, number] => [x * sx, y * sy])];
}
